import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  'https://us-central1-sistema-bks.cloudfunctions.net';

/**
 * FASE 3b — la página pública donde un profesional conecta su calendario.
 *
 * ⚠️ NO EXIGE SESIÓN Y NO DEBE CREAR NINGUNA. Quien llega aquí no es usuario de PUX, no
 * ocupa asiento y no va a volver a la aplicación. Entra, autoriza, y se va.
 *
 * ⚠️ NO REVELA NADA MÁS QUE EL NOMBRE DEL NEGOCIO Y EL DE SU AGENDA. Ni la organización, ni
 * las otras agendas, ni cuántas hay, ni el bot. Quien tenga el enlace puede no ser quien
 * debía —es un bearer token, ver `agendaInvite.js`— y no debe poder deducir el tamaño ni la
 * estructura del cliente. El nombre del negocio sí va: sin él la página es imposible de
 * confiar, y nadie debería autorizar su calendario a una pantalla que no dice de parte de
 * quién es.
 *
 * ⚠️ NO SE ESCRIBE EL RESULTADO AQUÍ. El texto final dice «falta que lo confirmen», que es
 * la verdad: el dueño tiene que ver con qué cuenta se conectó antes de que el bot la use.
 * Prometer «ya está listo» sería mentir en la mitad de los casos y, peor, haría que el
 * profesional no avisara a nadie de que ya autorizó.
 *
 * NO SE DISTINGUE «token inexistente» de «token caducado» de «token ya usado»: los tres
 * dan el mismo mensaje. Separarlos convertiría la página en un oráculo que confirma qué
 * tokens existieron.
 */
export default function ConectarAgenda() {
  const { token } = useParams();
  const [estado, setEstado] = useState('cargando');
  const [info, setInfo] = useState(null);
  const [yendo, setYendo] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/infoDeInvitacionAgenda?token=${encodeURIComponent(token || '')}`);
        const data = await res.json().catch(() => ({}));
        if (!vivo) return;
        if (!res.ok || !data.ok) { setEstado('invalido'); return; }
        setInfo(data);
        setEstado('listo');
      } catch {
        if (vivo) setEstado('invalido');
      }
    })();
    return () => { vivo = false; };
  }, [token]);

  const conectar = async (provider) => {
    setYendo(true);
    try {
      const res = await fetch(`${API_URL}/iniciarOAuthPorInvitacion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, provider }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.authUrl) { setEstado('invalido'); return; }
      window.location.href = data.authUrl;
    } catch {
      setEstado('invalido');
    } finally {
      setYendo(false);
    }
  };

  if (estado === 'cargando') {
    return <div className="conectar-agenda"><p>Cargando…</p></div>;
  }

  if (estado === 'invalido') {
    return (
      <div className="conectar-agenda">
        <h1>Este enlace ya no sirve</h1>
        <p>
          Puede que haya vencido, o que ya se haya usado. Pídele a quien te lo envió
          que te genere uno nuevo.
        </p>
      </div>
    );
  }

  return (
    <div className="conectar-agenda">
      <h1>Conectar tu calendario</h1>
      <p>
        {info.negocio
          ? <>Te pidieron conectar tu calendario para la agenda <strong>{info.agenda}</strong> de <strong>{info.negocio}</strong>.</>
          : <>Te pidieron conectar tu calendario para la agenda <strong>{info.agenda}</strong>.</>}
      </p>
      <p>
        Vamos a leer tu disponibilidad y crear las citas que se agenden. No creamos ninguna
        cuenta a tu nombre y puedes revocar el acceso desde tu cuenta de Google o Microsoft
        cuando quieras.
      </p>

      <div className="conectar-agenda__botones">
        <button type="button" disabled={yendo} onClick={() => conectar('google')}>
          Conectar con Google
        </button>
        <button type="button" disabled={yendo} onClick={() => conectar('microsoft')}>
          Conectar con Microsoft
        </button>
      </div>

      <p className="help-text">
        Después de autorizar, quien te envió el enlace tiene que confirmar que eres tú.
        Hasta entonces tu calendario no se usa.
      </p>
    </div>
  );
}

/**
 * La pantalla a la que vuelve el profesional tras autorizar. Fin del camino: no hay enlace
 * a la aplicación porque no tiene cuenta con la que entrar.
 *
 * CON `?error=` ES LA PANTALLA DEL FALLO (28-oct-2026). El callback manda aquí los errores del
 * flujo por invitación (`functions/integrations/calendarOAuthCallback.js`, `aError`): antes iban
 * a `/integrations`, que a quien no tiene cuenta le pedía iniciar sesión, y el aviso no llegaba.
 * El texto es el mismo `oauthErrors.<codigo>` de la app. Y se dice cómo seguir: el enlace ya
 * se consumió al empezar (defensa B, `agendasHandlers.js`), así que reintentar exige uno nuevo.
 */
// Los códigos con texto propio en la página PÚBLICA. Cualquier otro valor de `?error=` se pinta
// como el genérico y NO SE REPITE: la página no exige sesión, y repetir el parámetro dejaría a
// cualquiera escribir el texto que quisiera bajo nuestro dominio con un enlace fabricado
// (revisión independiente del 28-oct-2026).
const CODIGOS_CON_TEXTO_PUBLICO = ['calendar_scope_missing', 'state_expired'];

export function AgendaConectada() {
  const { t } = useTranslation('channels');
  const error = new URLSearchParams(window.location.search).get('error');

  if (error) {
    const clave = CODIGOS_CON_TEXTO_PUBLICO.includes(error) ? error : 'internal_error';
    return (
      <div className="conectar-agenda">
        <h1>No se pudo conectar tu calendario</h1>
        <p>{t(`oauthErrors.${clave}`)}</p>
        <p>
          El enlace que usaste ya no sirve: pídele a quien te lo envió que te genere uno nuevo.
        </p>
      </div>
    );
  }

  return (
    <div className="conectar-agenda">
      <h1>Listo</h1>
      <p>
        Tu calendario quedó autorizado. Falta que quien te envió el enlace lo confirme, y a
        partir de ahí empezarán a aparecer las citas.
      </p>
      <p>Ya puedes cerrar esta ventana.</p>
    </div>
  );
}
